import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Table,
  Tag,
  Switch,
  Badge,
  Button,
  Modal,
  Input,
  InputNumber,
  Checkbox,
  Select,
  Space,
  Spin,
  Popconfirm,
  Popover,
  Tooltip,
  message,
} from 'antd'
import { AlertOutlined, FilterOutlined } from '@ant-design/icons'
import ApolloClientService from '../../services/apolloclient.service'
import serviceInterface from '../../services/service.interface'

const { TextArea } = Input

// Admin brand navy ($main_color in css/common.scss) — matches the app's buttons.
const NAVY = '#012448'
const NAVY_BUTTON = { background: NAVY, borderColor: NAVY, color: '#fff' }

const CHANNEL_OPTIONS = [
  { label: 'Push', value: 'push' },
  { label: 'Email', value: 'email' },
  { label: 'SMS', value: 'sms' },
]

const CONDITION_LABELS = {
  EXERCISE_NOT_DONE_TODAY: 'exercise not done today',
  PROGRESS_TEST_NOT_DONE_TODAY: 'progress test not done today',
  MEASUREMENT_NOT_DONE_TODAY: 'measurement not done today',
  INACTIVE: 'patient inactive',
  WOUND_IMAGE_NOT_RECEIVED: 'wound photo not uploaded recently',
}

const CODE_REGEX = /^[A-Z0-9_]{1,12}$/

// Conditional reminders don't nudge on a schedule — they fire when something is
// wrong (exercise missed, patient inactive…), so surface them as alerts.
const isAlertRow = (row) => !!(row && row.trigger && row.trigger.kind === 'CONDITIONAL')

const AlertBadge = () => (
  <Tag color='volcano' icon={<AlertOutlined />}>
    alert
  </Tag>
)

// Template for the "Add reminder" flow — a scheduled daily nudge by default.
const NEW_REMINDER = {
  reminder_code: '',
  name: '',
  title: '',
  message: '',
  category: 'educational',
  channels: ['push'],
  trigger: { kind: 'SCHEDULED', dailyAt: '09:00' },
  active: true,
}

// A surgery day-offset as a readable phrase.
//   0 -> "surgery day", 3 -> "3 days after surgery", -2 -> "2 days before surgery".
function offsetPhrase(n) {
  if (n === 0) return 'surgery day'
  const days = Math.abs(n)
  const unit = days === 1 ? 'day' : 'days'
  return `${days} ${unit} ${n > 0 ? 'after' : 'before'} surgery`
}

// Readable phrase for a reminder's surgery window (dayOffsetMin/Max).
function windowPhrase(min, max) {
  const hasMin = min != null
  const hasMax = max != null
  if (hasMin && hasMax) {
    if (min === max) return min === 0 ? 'on surgery day' : offsetPhrase(min)
    return `from ${offsetPhrase(min)} to ${offsetPhrase(max)}`
  }
  if (hasMin) return `from ${offsetPhrase(min)} onward`
  if (hasMax) return `up to ${offsetPhrase(max)}`
  return null
}

// Human summary of a reminder's trigger for the table.
function triggerSummary(trigger) {
  if (!trigger || !trigger.kind) return '—'
  if (trigger.kind === 'SCHEDULED') {
    const time = trigger.dailyAt
    const anchored = trigger.dayOffsetFrom === 'surgery'
    const min = trigger.dayOffsetMin
    const max = trigger.dayOffsetMax
    // A single-day window (min === max) fires ONCE, not every day — so avoid the
    // word "Daily" there. "Daily" only applies when it repeats (a multi-day window
    // or no window at all).
    const singleDay = anchored && min != null && max != null && min === max
    const w = anchored ? windowPhrase(min, max) : null
    let prefix
    if (!time) prefix = 'Scheduled'
    else prefix = singleDay ? `At ${time}` : `Daily at ${time}`
    return w ? `${prefix} · ${w}` : prefix
  }
  if (trigger.kind === 'CONDITIONAL') {
    const cond = CONDITION_LABELS[trigger.condition] || trigger.condition
    return `If ${cond} by ${trigger.deadline || '—'}`
  }
  return trigger.kind
}

// Company- or clinic-scoped editor over the reminders catalog.
//   scope 'company' -> the org-wide defaults, layered system ⊕ company (keyed by
//                      companyid). Editing creates a company override.
//   scope 'clinic'  -> the clinic's OWN catalog (keyed by customerid): a full
//                      copy inherited from the company when the clinic was
//                      created. Company edits do not flow through; "Reset to …
//                      Default Reminders" re-copies the current company catalog.
function RemindersManager(props) {
  const { scope, companyid, customerid } = props
  // Company display name for clinic-scope copy ("Reset to MoveAI's Default
  // Reminders"); generic fallback when the page can't supply it.
  const companyLabel = props.companyName || 'Company'
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [rows, setRows] = useState([])
  const [editing, setEditing] = useState(null) // the working copy in the modal
  // Languages a reminder can carry besides English, from the server's
  // supportedLanguages (code/label/nativeLabel). One block per language in the
  // editor and one tag per language in the table — a new language on the
  // server shows up here with no admin change. Empty until it loads.
  const [languages, setLanguages] = useState([])
  // Table filter over "Applies to": 'all' shows everything, '' shows the
  // generic (all-patients) rows, otherwise an exact surgery type ('knee'…).
  const [typeFilter, setTypeFilter] = useState('all')
  // Clinic-level wound-photo switch (customer.allowPatientToSendWoundImages).
  // Fetched here rather than taken as a prop: this component is mounted from
  // two different pages, and the redux profile customer does not carry the
  // field. Starts TRUE so a failed or slow fetch never locks a control that
  // should be available -- the server-side gate in reminderengine.js is what
  // actually stops a send, so failing open here costs nothing.
  const [woundImagesAllowed, setWoundImagesAllowed] = useState(true)
  // Table filter over kind: scheduled rows are reminders, conditional are alerts.
  const [kindFilter, setKindFilter] = useState('all')

  const svc = serviceInterface.RemindersService()

  const load = async () => {
    setLoading(true)
    try {
      const res =
        scope === 'company'
          ? await svc.remindersForCompany({ companyid })
          : await svc.remindersForClinic({ customerid })
      // Group generic reminders first, then per-surgery-type timeline rows in
      // day order — keeps the table readable now that migrated events are in it.
      const list = ((res && res.data) || []).slice().sort((a, b) => {
        const stA = a.surgeryType || ''
        const stB = b.surgeryType || ''
        if (stA !== stB) return stA < stB ? -1 : 1
        const dA = (a.trigger && a.trigger.dayOffsetMin) != null ? a.trigger.dayOffsetMin : -Infinity
        const dB = (b.trigger && b.trigger.dayOffsetMin) != null ? b.trigger.dayOffsetMin : -Infinity
        if (dA !== dB) return dA - dB
        return String(a.name || '').localeCompare(String(b.name || ''))
      })
      setRows(list)
    } catch (e) {
      message.error('Failed to load reminders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    new ApolloClientService()
      .supportedLanguages()
      .then((list) => setLanguages((list || []).filter((l) => l && !l.isDefault)))
      .catch(() => {
        /* no languages -> no translation fields; the English editor still works */
      })
  }, [])

  useEffect(() => {
    if ((scope === 'company' && companyid) || (scope === 'clinic' && customerid)) load()
    if (scope === 'clinic' && customerid) {
      new ApolloClientService()
        .getCustomer(customerid)
        .then((res) => {
          const c = res && res.data ? res.data : res
          if (c && c.allowPatientToSendWoundImages === false)
            setWoundImagesAllowed(false)
        })
        .catch(() => {
          /* fail open -- see the note on the state above */
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, companyid, customerid])

  // Build the ReminderInput from a resolved row + patched fields.
  const buildInput = (row, patch) => {
    const merged = { ...row, ...patch }
    return {
      reminder_code: merged.reminder_code,
      name: merged.name,
      title: merged.title,
      message: merged.message,
      // { es: { title, message } }; the server drops blank fields, so a
      // cleared translation falls back to English.
      i18n: merged.i18n || {},
      category: merged.category,
      channels: merged.channels,
      trigger: merged.trigger,
      quietHours: merged.quietHours,
      smsType: merged.smsType,
      surgeryType: merged.surgeryType,
      notifyCaregiver: merged.notifyCaregiver,
      active: merged.active,
    }
  }

  const persist = async (row, patch) => {
    const input = buildInput(row, patch)
    if (scope === 'company') return svc.upsertCompanyReminder({ companyid, input })
    return svc.upsertClinicReminder({ customerid, input })
  }

  // Mirrors reminderengine.js `_isWoundReminder`: a clinic row can be created
  // with any code, so the CONDITION is the reliable signal. Either match wins.
  const isWoundRow = (row) => {
    if (!row) return false
    if (String(row.reminder_code || '').toUpperCase() === 'WOUND_IMAGE') return true
    const cond = (row.trigger && row.trigger.condition) || row.condition || ''
    return String(cond).toUpperCase() === 'WOUND_IMAGE_NOT_RECEIVED'
  }

  const toggleActive = async (row, active) => {
    try {
      await persist(row, { active })
      message.success(active ? 'Reminder enabled' : 'Reminder disabled')
      load()
    } catch (e) {
      message.error('Update failed')
    }
  }

  const saveEdit = async () => {
    // Codes are fixed once created — only the "Add reminder" flow sets one.
    const code = (editing.reminder_code || '').trim().toUpperCase()
    if (editing._isNew) {
      if (!CODE_REGEX.test(code)) {
        message.error('Code must be 1–12 chars: A–Z, 0–9, underscore')
        return
      }
      if (rows.some((r) => r.reminder_code === code)) {
        message.error(`Code ${code} is already in use`)
        return
      }
    }
    setSaving(true)
    try {
      await persist({ ...editing, reminder_code: code }, {})
      message.success('Reminder saved')
      setEditing(null)
      load()
    } catch (e) {
      message.error((e && e.message) || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // Company scope: drop the override row (reverts to the MoveAI default).
  // Clinic scope: remove a clinic-added reminder outright.
  const revert = async (row) => {
    try {
      if (scope === 'company') {
        await svc.deleteCompanyReminder({ companyid, reminder_code: row.reminder_code })
        message.success('Reverted to default')
      } else {
        await svc.deleteClinicReminder({ customerid, reminder_code: row.reminder_code })
        message.success('Reminder removed')
      }
      load()
    } catch (e) {
      message.error(scope === 'company' ? 'Revert failed' : 'Remove failed')
    }
  }

  // Clinic scope: wipe every clinic reminder and re-copy the company's current
  // catalog. Confirmed via modal — this discards all clinic edits and additions.
  const resetAllToCompany = () => {
    Modal.confirm({
      title: `Reset to ${companyLabel}'s Default Reminders`,
      content:
        'This will reset all your reminders back to the default reminder. Do you wish to proceed?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await svc.resetClinicReminders({ customerid })
          message.success('Reminders reset to company defaults')
          load()
        } catch (e) {
          message.error('Reset failed')
        }
      },
    })
  }

  const setEditField = (field, value) =>
    setEditing((prev) => ({ ...prev, [field]: value }))

  const setTriggerField = (field, value) =>
    setEditing((prev) => ({
      ...prev,
      trigger: { ...(prev.trigger || {}), [field]: value },
    }))

  // i18n.<lang>.<field> on the row being edited, e.g. the Spanish title.
  const setI18nField = (lang, field, value) =>
    setEditing((prev) => {
      const i18n = { ...(prev.i18n || {}) }
      i18n[lang] = { ...(i18n[lang] || {}), [field]: value }
      return { ...prev, i18n }
    })

  const i18nValue = (row, lang, field) =>
    (row && row.i18n && row.i18n[lang] && row.i18n[lang][field]) || ''

  // Set a surgery day-offset bound (dayOffsetMin/Max). When either bound is set the
  // trigger is anchored to surgery; when both are cleared it becomes a plain daily
  // reminder (no surgery window). Negative = before surgery (S−n), positive = after.
  const setDayOffset = (field, value) =>
    setEditing((prev) => {
      const trigger = { ...(prev.trigger || {}) }
      trigger[field] = value === null || value === undefined || value === '' ? undefined : value
      const anchored = trigger.dayOffsetMin != null || trigger.dayOffsetMax != null
      trigger.dayOffsetFrom = anchored ? 'surgery' : undefined
      return { ...prev, trigger }
    })

  const columns = [
    {
      title: 'Code',
      dataIndex: 'reminder_code',
      render: (code, row) => (
        <Space size={4}>
          <Tag
            color={isAlertRow(row) ? 'volcano' : 'blue'}
            style={{ fontFamily: 'monospace' }}
          >
            {code}
          </Tag>
          {isAlertRow(row) && <AlertBadge />}
        </Space>
      ),
    },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Trigger',
      dataIndex: 'trigger',
      render: (t) => <span style={{ fontSize: 12 }}>{triggerSummary(t)}</span>,
    },
    {
      // Migrated surgery-timeline rows are per surgery type; generic reminders
      // apply to every patient.
      title: 'Applies to',
      dataIndex: 'surgeryType',
      render: (st) =>
        st ? (
          <Tag color='purple' style={{ textTransform: 'capitalize' }}>{st}</Tag>
        ) : (
          <span style={{ fontSize: 12, color: '#999' }}>all patients</span>
        ),
    },
    {
      // One tag per language the row carries: green when title and message
      // are both there, orange when only one is, — when the row has none
      // (such a row goes out in English).
      title: 'Translations',
      dataIndex: 'i18n',
      width: 110,
      render: (i18n) => {
        const tags = languages
          .map((l) => {
            const texts = (i18n && i18n[l.code]) || {}
            const has = ['title', 'message'].filter((f) => texts[f] && String(texts[f]).trim())
            if (!has.length) return null
            return (
              <Tag key={l.code} color={has.length === 2 ? 'green' : 'orange'}>
                {l.code.toUpperCase()}
                {has.length === 2 ? '' : ' partial'}
              </Tag>
            )
          })
          .filter(Boolean)
        return tags.length ? tags : <span style={{ fontSize: 12, color: '#999' }}>—</span>
      },
    },
    {
      title: 'Channels',
      dataIndex: 'channels',
      render: (chs) =>
        (chs || []).map((c) => (
          <Tag key={c} style={{ textTransform: 'capitalize' }}>{c}</Tag>
        )),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      render: (active, row) => {
        // A clinic that has switched wound photos off must not be able to turn
        // this reminder on: the patient app hides the SEND WOUND IMAGE button,
        // so the reminder would ask for something they cannot do.
        //
        // Shown as off and disabled rather than written to false. Persisting a
        // change as a side effect of rendering a table is the kind of silent
        // write nobody can later explain, and it is unnecessary -- the engine
        // refuses to send it regardless (reminderengine.js _isWoundReminder).
        const locked = !woundImagesAllowed && isWoundRow(row)
        const sw = (
          <Switch
            checked={!!active && !locked}
            disabled={locked}
            onChange={(v) => toggleActive(row, v)}
          />
        )
        return locked ? (
          <Tooltip title="Turned off because this clinic has 'Allow patients to send wound images' set to Off. Turn that on first.">
            <span>{sw}</span>
          </Tooltip>
        ) : (
          sw
        )
      },
    },
    {
      title: 'Actions',
      render: (_, row) => (
        <Space>
          <Button
            size='small'
            onClick={() => setEditing({ ...row })}
          >
            Edit
          </Button>
          {scope === 'company' && row.effectiveScope === 'company' && (
            <Popconfirm
              title='Remove this company override and revert to the MoveAI default?'
              onConfirm={() => revert(row)}
            >
              <Button size='small'>Reset to default</Button>
            </Popconfirm>
          )}
          {/* Clinic-added reminders aren't part of the company catalog, so the
              only sensible action is removing them outright. Inherited rows have
              no per-row reset — use "Reset to … Default Reminders" up top. */}
          {scope === 'clinic' && row.isCustom && (
            <Popconfirm
              title='Remove this reminder from this clinic?'
              onConfirm={() => revert(row)}
            >
              <Button size='small' danger>
                Remove
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  const t = (editing && editing.trigger) || {}
  const isConditional = t.kind === 'CONDITIONAL'

  // "Applies to" options — the clinic's configured discipline (active surgery
  // types passed in by the page) when available, else the types present in the
  // catalog rows. Constrained to known values on purpose: a typo'd type would
  // silently stop the reminder for every patient (no surgery.type would match).
  const knownTypes =
    props.surgeryTypes && props.surgeryTypes.length
      ? props.surgeryTypes
      : rows.map((r) => r.surgeryType).filter(Boolean)
  const surgeryTypeOptions = Array.from(new Set(knownTypes)).map((st) => ({
    label: st.charAt(0).toUpperCase() + st.slice(1),
    value: st,
  }))

  const filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'All patients', value: '' },
    ...surgeryTypeOptions,
  ]
  const kindOptions = [
    { label: 'All', value: 'all' },
    { label: 'Reminders', value: 'reminders' },
    { label: 'Alerts', value: 'alerts' },
  ]
  const filtersActive = typeFilter !== 'all' || kindFilter !== 'all'
  const filteredRows = rows.filter((r) => {
    if (typeFilter !== 'all' && (r.surgeryType || '') !== typeFilter) return false
    if (kindFilter === 'alerts') return isAlertRow(r)
    if (kindFilter === 'reminders') return !isAlertRow(r)
    return true
  })

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 12, color: '#666', paddingRight: 16 }}>
          {scope === 'company'
            ? 'These are your organisation-wide default reminders. New clinics inherit a copy of this list when they are created; editing it does not change existing clinics.'
            : "These are this clinic's reminders — a copy inherited from the company when the clinic was created. Edits here apply only to this clinic; disabling one turns it off for every patient in this clinic."}
        </div>
        <Space>
          {scope === 'clinic' && (
            <Button onClick={resetAllToCompany}>
              Reset to {companyLabel}&apos;s Default Reminders
            </Button>
          )}
          {props.onCancel && <Button onClick={props.onCancel}>Cancel</Button>}
          <Button
            type='primary'
            style={NAVY_BUTTON}
            onClick={() =>
              setEditing({
                ...NEW_REMINDER,
                trigger: { ...NEW_REMINDER.trigger },
                channels: [...NEW_REMINDER.channels],
                _isNew: true,
              })
            }
          >
            Add reminder
          </Button>
        </Space>
      </div>
      <Space size={8} wrap style={{ marginBottom: 12 }}>
        <Popover
          trigger='click'
          placement='bottomLeft'
          content={
            <Space direction='vertical' size={12}>
              <div>
                <div style={{ fontSize: 12, color: NAVY, fontWeight: 600, marginBottom: 4 }}>
                  Applies to
                </div>
                <Select
                  style={{ width: 180 }}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={filterOptions}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: NAVY, fontWeight: 600, marginBottom: 4 }}>
                  Type
                </div>
                <Select
                  style={{ width: 180 }}
                  value={kindFilter}
                  onChange={setKindFilter}
                  options={kindOptions}
                />
              </div>
              {filtersActive && (
                <Button
                  size='small'
                  onClick={() => {
                    setTypeFilter('all')
                    setKindFilter('all')
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Space>
          }
        >
          <Badge dot={filtersActive}>
            <Button size='small' style={NAVY_BUTTON} icon={<FilterOutlined />}>
              Filter
            </Button>
          </Badge>
        </Popover>
        {typeFilter !== 'all' && (
          <Tag closable onClose={() => setTypeFilter('all')}>
            {(filterOptions.find((o) => o.value === typeFilter) || {}).label}
          </Tag>
        )}
        {kindFilter !== 'all' && (
          <Tag closable onClose={() => setKindFilter('all')}>
            {(kindOptions.find((o) => o.value === kindFilter) || {}).label}
          </Tag>
        )}
      </Space>
      <Table
        rowKey='reminder_code'
        size='small'
        columns={columns}
        dataSource={filteredRows}
        pagination={false}
      />

      <Modal
        title={
          editing ? (
            editing._isNew ? (
              'Add reminder'
            ) : (
              <span>
                {isAlertRow(editing) ? 'Edit alert' : 'Edit reminder'}{' '}
                <Tag
                  color={isAlertRow(editing) ? 'volcano' : 'blue'}
                  style={{ fontFamily: 'monospace' }}
                >
                  {editing.reminder_code}
                </Tag>
                {isAlertRow(editing) && <AlertBadge />}
              </span>
            )
          ) : (
            ''
          )
        }
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={saveEdit}
        confirmLoading={saving}
        okText='Save'
        destroyOnClose
      >
        {editing && (
          <Space direction='vertical' style={{ width: '100%' }} size={12}>
            {editing._isNew && (
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>
                  Code (1–12 chars, A–Z / 0–9 / _) — fixed once created
                </label>
                <Input
                  style={{ width: 200, fontFamily: 'monospace' }}
                  placeholder='MEASUREMENT'
                  value={editing.reminder_code}
                  maxLength={12}
                  onChange={(e) =>
                    setEditField(
                      'reminder_code',
                      e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '')
                    )
                  }
                />
              </div>
            )}
            {editing._isNew && (
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Trigger type</label>
                <br />
                <Select
                  style={{ width: 260 }}
                  value={(editing.trigger && editing.trigger.kind) || 'SCHEDULED'}
                  onChange={(kind) =>
                    setEditField(
                      'trigger',
                      kind === 'CONDITIONAL'
                        ? {
                            kind: 'CONDITIONAL',
                            condition: 'EXERCISE_NOT_DONE_TODAY',
                            deadline: '18:00',
                          }
                        : { kind: 'SCHEDULED', dailyAt: '09:00' }
                    )
                  }
                  options={[
                    { label: 'Scheduled (fixed time)', value: 'SCHEDULED' },
                    { label: 'Conditional alert (if not done by…)', value: 'CONDITIONAL' },
                  ]}
                />
              </div>
            )}
            {editing._isNew && isConditional && (
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Condition</label>
                <br />
                <Select
                  style={{ width: 260 }}
                  value={t.condition}
                  onChange={(v) => setTriggerField('condition', v)}
                  options={Object.keys(CONDITION_LABELS).map((c) => ({
                    label: CONDITION_LABELS[c],
                    value: c,
                  }))}
                />
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: '#666' }}>Name</label>
              <Input
                value={editing.name}
                onChange={(e) => setEditField('name', e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666' }}>Notification title</label>
              <Input
                value={editing.title}
                onChange={(e) => setEditField('title', e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666' }}>
                Message (supports {'{firstname}'})
              </label>
              <TextArea
                rows={3}
                value={editing.message}
                onChange={(e) => setEditField('message', e.target.value)}
              />
            </div>
            {languages.map((l) => (
              <div
                key={l.code}
                style={{
                  border: '1px solid #e8e8e8',
                  borderRadius: 4,
                  padding: '8px 12px 12px',
                  background: '#fafafa',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                  {l.label}
                  {l.nativeLabel && l.nativeLabel !== l.label ? ` (${l.nativeLabel})` : ''}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                  Sent to patients who use the app in {l.label} — push, email and the
                  in-app card. Leave both blank to send them the English.
                </div>
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>
                      Notification title ({l.label})
                    </label>
                    <Input
                      value={i18nValue(editing, l.code, 'title')}
                      onChange={(e) => setI18nField(l.code, 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>
                      Message ({l.label}, supports {'{firstname}'})
                    </label>
                    <TextArea
                      rows={3}
                      value={i18nValue(editing, l.code, 'message')}
                      onChange={(e) => setI18nField(l.code, 'message', e.target.value)}
                    />
                  </div>
                </Space>
              </div>
            ))}
            <div>
              <label style={{ fontSize: 12, color: '#666' }}>Channels</label>
              <Checkbox.Group
                options={CHANNEL_OPTIONS}
                value={editing.channels || []}
                onChange={(vals) => setEditField('channels', vals)}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666' }}>
                Applies to — leave empty for all patients
              </label>
              <br />
              <Select
                allowClear
                placeholder='All patients'
                style={{ width: 200 }}
                value={editing.surgeryType || undefined}
                // '' (not null) is the explicit "all patients" override: the
                // layered merge ignores null fields, so null could never widen a
                // per-type parent row, while '' wins the merge and the engine
                // treats any falsy surgeryType as "applies to everyone".
                onChange={(v) => setEditField('surgeryType', v || '')}
                options={surgeryTypeOptions}
              />
            </div>
            {isConditional ? (
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>
                  Condition: <b>{CONDITION_LABELS[t.condition] || t.condition}</b> — deadline
                  (local HH:MM)
                </label>
                <Input
                  style={{ width: 120 }}
                  placeholder='18:00'
                  value={t.deadline || ''}
                  onChange={(e) => setTriggerField('deadline', e.target.value)}
                />
              </div>
            ) : (
              <>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>
                    Send daily at (local HH:MM)
                  </label>
                  <Input
                    style={{ width: 120 }}
                    placeholder='09:00'
                    value={t.dailyAt || ''}
                    onChange={(e) => setTriggerField('dailyAt', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>
                    Days from surgery — negative is before (S−n), positive is after
                    (S+n). Leave both blank to send every day.
                  </label>
                  <Space align='center'>
                    <span style={{ fontSize: 12, color: '#666' }}>From</span>
                    <InputNumber
                      style={{ width: 90 }}
                      placeholder='-2'
                      value={t.dayOffsetMin != null ? t.dayOffsetMin : null}
                      onChange={(v) => setDayOffset('dayOffsetMin', v)}
                    />
                    <span style={{ fontSize: 12, color: '#666' }}>To</span>
                    <InputNumber
                      style={{ width: 90 }}
                      placeholder='18'
                      value={t.dayOffsetMax != null ? t.dayOffsetMax : null}
                      onChange={(v) => setDayOffset('dayOffsetMax', v)}
                    />
                  </Space>
                </div>
              </>
            )}
          </Space>
        )}
      </Modal>
    </div>
  )
}

RemindersManager.propTypes = {
  scope: PropTypes.oneOf(['company', 'clinic']).isRequired,
  companyid: PropTypes.string,
  customerid: PropTypes.string,
  // Company display name, used by clinic-scope copy ("Reset to X's Default Reminders").
  companyName: PropTypes.string,
  // The clinic's active surgery-type names (lowercase), e.g. ['knee','hip','pt'].
  // Optional — without it the "Applies to" options derive from the catalog rows.
  surgeryTypes: PropTypes.arrayOf(PropTypes.string),
  // Collapses the settings panel. Reminders commit per-action (no staged Save),
  // so this just closes the section rather than discarding pending edits.
  onCancel: PropTypes.func,
}

export default RemindersManager
